import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAuth } from "../hooks/useAuth";
import { useInactivityWarning } from "../hooks/useInactivityWarning";
import AdminChatDockCard from "./cards/AdminChatDockCard";
import AdminGreyDockCard from "./cards/AdminGreyDockCard";
import RegularUserChatDockCard from "./cards/RegularUserChatDockCard";
import BlogCard from "./cards/BlogCard";
import UserDetailsDockCard from "./cards/UserDetailsDockCard";
import YellowDockCard from "./cards/YellowDockCard";
import GlobalHeader from "./GlobalHeader";
import GlobalFooter from "./GlobalFooter";
import BackgroundLogo from "./BackgroundLogo";
import { InactivityWarningModal } from "./InactivityWarningModal";
import { toast } from "sonner";

const CARD_GAP = 4;
const SWIPE_THRESHOLD = 120;
const WHEEL_THRESHOLD = 60;
const LOGO_REVEAL_DELAY = 200;

const CarlineLayout: React.FC = () => {
  const isMobile = useIsMobile();
  const { user, profile, signOut, refreshSession, isAdmin } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isChatResisting, setIsChatResisting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const wheelLockRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [logoComplete, setLogoComplete] = useState(false);
  const location = useLocation();

  // Inactivity warning system
  const { showWarning, timeRemaining, handleStillHere, handleLogout } = useInactivityWarning(
    !!user,
    signOut,
    refreshSession,
  );

  useLayoutEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const updateSizes = () => {
      const width = container.offsetWidth;
      setContainerWidth(width);
      const nextCardWidth = Math.max(280, width - CARD_GAP * 2);
      setCardWidth(nextCardWidth);
    };

    updateSizes();
    const observer = new ResizeObserver(updateSizes);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!logoComplete) return;
    const timer = window.setTimeout(() => {
      setCardsVisible(true);
    }, LOGO_REVEAL_DELAY);

    return () => window.clearTimeout(timer);
  }, [logoComplete]);

  useLayoutEffect(() => {
    if (!cardsVisible) return;

    const timer = window.setTimeout(() => {
      setIntroComplete(true);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [cardsVisible]);

  const handleChatTeaser = () => {
    if (isAdmin) return;
    setIsChatResisting(true);
    setTimeout(() => setIsChatResisting(false), 500);
    toast.info("Chat card coming soon! Follow the blog for updates.", {
      duration: 3000,
    });
  };

  const isCarouselLocked = !isAdmin;

  const shouldIgnoreDrag = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    const interactiveTags = ["INPUT", "TEXTAREA", "BUTTON", "A", "SELECT", "OPTION"];
    if (interactiveTags.includes(target.tagName)) return true;
    // Check if target is inside an interactive element (works for SVG elements too)
    const closestInteractive = target.closest("button, a, input, textarea, select, [role='button']");
    if (closestInteractive) return true;
    return Boolean(target.closest("[data-no-drag='true']"));
  };

  const handleWheel = (event: React.WheelEvent) => {
    if (isCarouselLocked) return;
    if (shouldIgnoreDrag(event.target)) return;
    if (wheelLockRef.current) return;
    if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
    if (Math.abs(event.deltaX) < WHEEL_THRESHOLD) return;

    wheelLockRef.current = true;
    const direction = event.deltaX > 0 ? 1 : -1;
    setActiveIndex((prev) => {
      const next = Math.min(Math.max(prev + direction, 0), lastIndex);
      return next;
    });
    window.setTimeout(() => {
      wheelLockRef.current = false;
    }, 400);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isCarouselLocked) return;
    if (event.button !== 0) return;
    if (shouldIgnoreDrag(event.target)) return;
    dragStartRef.current = event.clientX;
    dragOffsetRef.current = 0;
    setDragOffset(0);
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isCarouselLocked) return;
    if (!isDragging) return;
    const delta = event.clientX - dragStartRef.current;
    const isAtStart = activeIndex === 0;
    const isAtEnd = activeIndex === lastIndex;
    let adjustedDelta = delta;

    if ((isAtStart && delta > 0) || (isAtEnd && delta < 0)) {
      adjustedDelta = delta * 0.35;
    }

    dragOffsetRef.current = adjustedDelta;
    setDragOffset(adjustedDelta);
  };

  const handlePointerEnd = () => {
    if (isCarouselLocked) return;
    if (!isDragging) return;
    setIsDragging(false);

    const delta = dragOffsetRef.current;
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta < 0) {
        setActiveIndex((prev) => Math.min(prev + 1, lastIndex));
      } else {
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      }
    }

    dragOffsetRef.current = 0;
    setDragOffset(0);
  };

  const fullCardStack = [
    {
      id: "chat",
      title: "Chat",
      accent: "bg-chat-red",
      textColor: "text-white",
      content: (
        <div className="flex h-full flex-col">{isAdmin ? <AdminChatDockCard /> : <RegularUserChatDockCard />}</div>
      ),
      onClick: handleChatTeaser,
      shouldResist: !isAdmin && isChatResisting,
    },
    {
      id: "details",
      title: "Profile",
      accent: "bg-criteria-blue",
      textColor: "text-white",
      content: <UserDetailsDockCard />,
    },
    {
      id: "planning",
      title: "Planning",
      accent: "bg-results-yellow",
      textColor: "text-foreground",
      content: <YellowDockCard />,
    },
    {
      id: "blog",
      title: "Blog",
      accent: "bg-blog-white",
      textColor: "text-foreground",
      content: (
        <div className="h-full w-full">
          <BlogCard layout="carousel" cardState="fullscreen" isMobile={isMobile} onHideCard={() => undefined} />
        </div>
      ),
    },
    {
      id: "admin",
      title: "Admin",
      accent: "bg-admin-grey",
      textColor: "text-white",
      content: <AdminGreyDockCard />,
    },
  ];

  const cardStack = isAdmin ? fullCardStack : fullCardStack.filter((card) => card.id === "chat" || card.id === "blog");
  const blogIndex = cardStack.findIndex((card) => card.id === "blog");
  const hasPostInUrl = useMemo(() => {
    if (location.pathname.startsWith("/post/")) {
      return true;
    }
    const params = new URLSearchParams(location.search);
    return params.has("post");
  }, [location.pathname, location.search]);

  useEffect(() => {
    setActiveIndex((prev) => {
      if (isCarouselLocked && blogIndex !== -1) {
        return blogIndex;
      }
      return Math.min(prev, cardStack.length - 1);
    });
  }, [blogIndex, cardStack.length, isCarouselLocked]);

  useEffect(() => {
    if (hasPostInUrl && blogIndex !== -1) {
      setActiveIndex(blogIndex);
    }
  }, [blogIndex, hasPostInUrl]);

  const lastIndex = cardStack.length - 1;
  const previousCard = activeIndex > 0 ? cardStack[activeIndex - 1] : null;
  const nextCard = activeIndex < lastIndex ? cardStack[activeIndex + 1] : null;
  const leftCount = activeIndex;
  const rightCount = lastIndex - activeIndex;
  const inactiveCardWidth = isMobile ? Math.max(220, cardWidth - 110) : cardWidth;
  const activeCardWidth = isMobile ? Math.min(containerWidth - 4, cardWidth + 90) : cardWidth;
  const cardWidths = cardStack.map((_, index) =>
    isMobile ? (index === activeIndex ? activeCardWidth : inactiveCardWidth) : cardWidth,
  );
  const activeWidth = cardWidths[activeIndex] ?? cardWidth;
  const totalLeftWidth = cardWidths.slice(0, activeIndex).reduce((sum, width) => sum + width, 0);
  const baseOffset = (containerWidth - activeWidth) / 2;
  const translateX = baseOffset - totalLeftWidth - activeIndex * CARD_GAP + dragOffset;

  return (
    <>
      <BackgroundLogo onAnimationComplete={() => setLogoComplete(true)} />

      <GlobalHeader
        user={user}
        profile={profile}
        onLogout={() => {
          signOut();
        }}
        shouldRetract={true}
      />

      <GlobalFooter shouldRetract={true} />

      <div className="relative z-10 h-[100dvh] overflow-hidden font-sans">
        <div className="relative flex h-full flex-col items-center justify-center px-4 pt-0 pb-10 box-border">
          {/* Wrapper for carousel + navigation buttons - buttons positioned here to avoid transform clipping */}
          <div className="relative w-full">
            <div
              ref={carouselRef}
              className={`w-full touch-pan-y transition-all duration-700 ease-out ${
                cardsVisible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-8 opacity-0"
              }`}
              onWheel={handleWheel}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerLeave={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
            >
              <div
                className="flex items-center"
                style={{
                  transform: `translateX(${translateX}px)`,
                  gap: `${CARD_GAP}px`,
                  transition: isDragging ? "none" : "transform 0.45s ease",
                }}
              >
                {cardStack.map((card, index) => (
                  <div
                    key={card.id}
                    data-card={card.id}
                    className="shrink-0 transition-[width] duration-500 ease-out"
                    style={{
                      width: cardWidths[index] ?? cardWidth,
                      zIndex: index === activeIndex ? 10 : 1,
                    }}
                  >
                    <div
                      className={`relative flex w-full flex-col overflow-hidden rounded-3xl border border-white/10 shadow-2xl transition-[height] duration-500 ease-out ${card.accent} ${card.textColor} ${
                        card.shouldResist ? "animate-bounce-resist" : ""
                      } ${index === 0 && !introComplete ? "animate-slide-in-up" : ""}`}
                      style={{
                        height: isMobile
                          ? index === activeIndex
                            ? "calc(100dvh - 3.00rem)"
                            : "calc(100dvh - 6.75rem)"
                          : "calc(100dvh - 5.5rem)",
                      }}
                      onClick={() => {
                        if (!isCarouselLocked && index !== activeIndex) {
                          setActiveIndex(index);
                        }
                        card.onClick?.();
                      }}
                    >
                      {card.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation buttons positioned outside carousel transform context */}
            {cardsVisible && !isCarouselLocked && previousCard && (
              <button
                type="button"
                aria-label={`Previous card: ${previousCard.title}`}
                onClick={() => setActiveIndex((prev) => Math.max(prev - 1, 0))}
                className="group absolute bottom-20 left-2 z-30 flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-2 py-1.5 shadow-lg transition hover:bg-background sm:bottom-24 sm:left-0 sm:gap-2.5 sm:bg-background/90 sm:px-3 sm:py-2"
              >
                <ChevronLeft className="h-4 w-4 text-muted-foreground transition group-hover:text-foreground" />
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-semibold text-foreground">
                    {previousCard.title}
                    {leftCount > 1 && (
                      <span className="ml-1 text-xs font-medium text-muted-foreground">+{leftCount - 1}</span>
                    )}
                  </p>
                </div>
              </button>
            )}

            {cardsVisible && !isCarouselLocked && nextCard && (
              <button
                type="button"
                aria-label={`Next card: ${nextCard.title}`}
                onClick={() => setActiveIndex((prev) => Math.min(prev + 1, lastIndex))}
                className="group absolute bottom-20 right-2 z-30 flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-2 py-1.5 shadow-lg transition hover:bg-background sm:bottom-24 sm:right-0 sm:gap-2.5 sm:bg-background/90 sm:px-3 sm:py-2"
              >
                <div className="hidden text-right sm:block">
                  <p className="text-sm font-semibold text-foreground">
                    {nextCard.title}
                    {rightCount > 1 && (
                      <span className="ml-1 text-xs font-medium text-muted-foreground">+{rightCount - 1}</span>
                    )}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition group-hover:text-foreground" />
              </button>
            )}
          </div>

          {cardsVisible && !isCarouselLocked && (
            <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-3">
              {cardStack.map((card, index) => (
                <button
                  key={card.id}
                  type="button"
                  aria-label={`Go to ${card.title}`}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2.5 w-2.5 rounded-full transition-all ${
                    index === activeIndex ? "bg-foreground" : "bg-muted-foreground/40"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <InactivityWarningModal
        open={showWarning}
        timeRemaining={timeRemaining}
        onStillHere={handleStillHere}
        onLogout={handleLogout}
      />
    </>
  );
};

export default CarlineLayout;
